import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { BASE_URL } from "../../config/RequestMethod";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Flex,
  GridItem,
  HStack,
  Icon,
  Image,
  Link,
  SimpleGrid,
  Skeleton,
  Tag,
  Text,
  VStack,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { A4ResponsiveWrapper } from "../proposal/components/A4ResponsiveWrapper";
import { A4Box } from "../proposal/ProposalView";
import bgImage from "../../icons/logo.jpg";
import bottomImage from "../../icons/pdfbg.png";
import {
  MdEmail,
  MdLocationOn,
  MdPhone,
  MdNewspaper,
  MdCompare,
} from "react-icons/md";
import {
  FaCheck,
  FaDownload,
  FaPrint,
  FaTimes,
  FaWhatsapp,
  FaGlobe,
} from "react-icons/fa";
import {
  ArrowLeft,
  ChevronRight,
  Home,
  FileText,
  Menu,
  Download,
  Printer,
  CheckCircle,
} from "lucide-react";
import HtmlPaginated from "../proposal/components/HtmlPaginated ";
import NotFound from "../not-found/NotFound";
import Unauthorized from "../Unauthorized/Unauthorized";
import ESignCanvas from "./ESignCanvas";
import ProposalInfoSkeleton from "../proposal/components/ProposalInfoSkeleton ";
import html2pdf from "html2pdf.js";
import ThankYou from "./ThankYou";
import DeclineForm from "./DeclineForm";
import SeeYouNext from "./SeeYouNext";

interface Proposal {
  Esign: string;
  status: string;
  sentDate: Date;
  clientReactionDate: Date;
  clientNote: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  greetingId: string;
  descriptions: {
    value: string;
  };
  terms: {
    value: string;
  };
  greeting: string;
  emiSteps: {
    label: string;
    payPercentage: number;
    pay: number;
    isPaid: boolean;
    remark: string;
    emiPaymentReceivedDate: Date;
    emiPaymentDate: Date;
    emiPayUpdatedBy: {
      name: string;
      userId: string;
    };
  }[];
  discountType: string;
  subTotal: number;
  discount: string;
  discountPrice: number;
  total: number;
  totalTax: number;
  validTillDate: Date;
  proposalName: string;
  currency: string;
  items: {
    productId: string;
    quantity: number;
    productName: string;
    price: number;
    tax: string;
    total: number;
    totalWithTax: number;
  }[];
  createdBY: {
    name: string;
    userId: string;
  };
  companyInfo: {
    _id: string;
    companyName: string;
    Name: string;
    Email: string;
    Phone: string;
    Address: string;
    webURL: string;
    City: string;
    ComapanyImageOne: string;
    ComapanyImageTwo: string;
  };
}

const ClientProposalView = () => {
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get("proposalId");

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [showAccept, setShowAccept] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [showDecline, setShowDecline] = useState<boolean>(false);
  const [showSeeYouNext, setShowSeeYouNext] = useState<boolean>(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const pageRefs = useRef<{ [key: number]: HTMLElement | null }>({});

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    await new Promise((resolve) => setTimeout(resolve, 100));

    const element = document.querySelector(".print-area");

    html2pdf()
      .set({
        margin: 0,
        filename: `${proposal?.proposalName || "proposal"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "px", format: [794, 1122], orientation: "portrait" },
      })
      .from(element)
      .save()
      .finally(() => setIsGeneratingPDF(false));
  };

  const getProposalDetails = async () => {
    try {
      setLoading(true);
      const res: any = await axios.get(
        `${BASE_URL}client-proposal/${proposalId}`
      );
      console.log("Proposal Data:", res?.data?.Data);
      setProposal(res?.data?.Data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else if (err.response?.status === 403) {
        setUnauthorized(true);
      } else {
        console.error("Unexpected error", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proposalId) {
      getProposalDetails();
    }
  }, [proposalId]);

  const scrollToPage = (pageNum: number) => {
    onClose();
    setTimeout(() => {
      const element =
        pageRefs.current[pageNum] ||
        document.getElementById(`page-${pageNum}`) ||
        document.querySelector(`#page-${pageNum}`);

      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
        setCurrentPage(pageNum);
      }
    }, 100);
  };

  if (notFound) return <NotFound />;
  if (unauthorized) return <Unauthorized />;
  if (showThankYou) return <ThankYou onBack={() => setShowThankYou(false)} proposal={proposal}/>;
  if (showSeeYouNext)
    return (
      <SeeYouNext onBack={() => setShowSeeYouNext(false)} proposal={proposal} />
    );

  const totalPages = 5;

  return (
    <Box bg="gray.50" minH="100vh" overflow="hidden">
      {/* Modern Header - Hidden in print */}
      <Box
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
        className="no-print"
      >
        <Flex
          justify="space-between"
          align="center"
          px={{ base: 4, md: 8 }}
          py={4}
          maxW="1400px"
          mx="auto"
        >
          {/* Left - Title */}
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              Proposal Preview
            </Text>
            <Text fontSize="xs" color="gray.500">
              {proposal?.proposalName || "Loading..."}
            </Text>
          </VStack>

          {/* Right - Actions */}
          <HStack spacing={2}>
            {/* Menu Button - Mobile/Tablet Only */}
            <Button
              leftIcon={<Menu size={16} />}
              onClick={onOpen}
              variant="outline"
              colorScheme="gray"
              size={{ base: "sm", md: "md" }}
              display={{ base: "flex", lg: "none" }}
              fontSize="sm"
            >
              <Text display={{ base: "none", md: "inline" }}>Pages</Text>
            </Button>

            {/* Print Button */}
            <Button
              leftIcon={<Printer size={16} />}
              onClick={handlePrint}
              size={{ base: "sm", md: "md" }}
              fontSize="sm"
              fontWeight="600"
              bgGradient="linear(to-r, blue.400, blue.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, blue.500, blue.700)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px -4px rgba(59, 130, 246, 0.4)",
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
            >
              <Text display={{ base: "none", md: "inline" }}>Print</Text>
            </Button>

            {/* Download Button */}
            <Button
              leftIcon={<Download size={16} />}
              onClick={handleDownloadPDF}
              size={{ base: "sm", md: "md" }}
              fontSize="sm"
              fontWeight="600"
              bgGradient="linear(to-r, purple.400, purple.600)"
              color="white"
              isLoading={isGeneratingPDF}
              _hover={{
                bgGradient: "linear(to-r, purple.500, purple.700)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px -4px rgba(147, 51, 234, 0.4)",
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
            >
              <Text display={{ base: "none", md: "inline" }}>Download</Text>
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Page Navigator Drawer - For Mobile/Tablet */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        size={{ base: "xs", md: "sm" }}
      >
        <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient="linear(to-r, blue.500, blue.600)"
            color="white"
          >
            <HStack spacing={2}>
              <FileText size={20} />
              <Text>Page Navigator</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody p={4}>
            <VStack spacing={3} align="stretch">
              {[
                { num: 1, title: "Greeting" },
                { num: 2, title: "Description" },
                { num: 3, title: "Items" },
                { num: 4, title: "Payment" },
                { num: 5, title: "Terms" },
              ].map((page) => (
                <Box
                  key={page.num}
                  onClick={() => scrollToPage(page.num)}
                  cursor="pointer"
                  bg="white"
                  border="2px solid"
                  borderColor={
                    currentPage === page.num ? "blue.500" : "gray.200"
                  }
                  borderRadius="lg"
                  p={3}
                  transition="all 0.2s"
                  _hover={{
                    borderColor: "blue.400",
                    transform: "translateX(4px)",
                    boxShadow: "md",
                  }}
                  _active={{ transform: "scale(0.98)" }}
                >
                  <HStack spacing={2}>
                    <Box
                      bg={currentPage === page.num ? "blue.500" : "gray.200"}
                      color={currentPage === page.num ? "white" : "gray.600"}
                      w={8}
                      h={8}
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {page.num}
                    </Box>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="700" color="gray.800">
                        Page {page.num}
                      </Text>
                      <Text fontSize="xs" fontWeight="500" color="gray.600">
                        {page.title}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main Content with Sidebar */}
      <Flex
        maxW="1400px"
        mx="auto"
        gap={4}
        p={{ base: 2, md: 4 }}
        overflow="hidden"
      >
        {/* Left Sidebar - Desktop Only */}
        <Box
          w="200px"
          display={{ base: "none", lg: "block" }}
          position="fixed"
          top="180px"
          h="calc(100vh - 140px)"
          overflowY="auto"
          className="no-print"
          css={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { bg: "gray.100" },
            "&::-webkit-scrollbar-thumb": {
              bg: "gray.400",
              borderRadius: "full",
            },
          }}
        >
          <VStack spacing={3} align="stretch" p={2}>
            {[
              { num: 1, title: "Greeting" },
              { num: 2, title: "Description" },
              { num: 3, title: "Items" },
              { num: 4, title: "Payment" },
              { num: 5, title: "Terms" },
            ].map((page) => (
              <Box
                key={page.num}
                onClick={() => scrollToPage(page.num)}
                cursor="pointer"
                bg="white"
                border="2px solid"
                borderColor={currentPage === page.num ? "blue.500" : "gray.200"}
                borderRadius="lg"
                p={3}
                transition="all 0.2s"
                _hover={{
                  borderColor: "blue.400",
                  transform: "translateX(4px)",
                  boxShadow: "md",
                }}
              >
                <HStack spacing={2}>
                  <Box
                    bg={currentPage === page.num ? "blue.500" : "gray.200"}
                    color={currentPage === page.num ? "white" : "gray.600"}
                    w={6}
                    h={6}
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {page.num}
                  </Box>
                  <Text fontSize="xs" fontWeight="600" color="gray.700">
                    {page.title}
                  </Text>
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Main Preview Area */}
        <Box flex={1} overflow="hidden" ml={{ base: 0, lg: "220px" }}>
          {!showAccept && !showDecline && (
            <VStack
              align="center"
              gap={isGeneratingPDF ? 0 : 4}
              spacing={0}
              pb={8}
              className="print-area"
            >
              <A4ResponsiveWrapper isDownload={isGeneratingPDF}>
                {/* Page 1: Greeting - Modern Gradient Design */}
                <Box
                  position="relative"
                  w="794px"
                  h="1122px"
                  bg="white"
                  boxShadow="sm"
                  className="page"
                  overflow="hidden"
                  id="page-1"
                >
                  <Box ref={(el) => (pageRefs.current[1] = el)} h="full">
                    {loading ? (
                      <Box px={10} py={10}>
                        <ProposalInfoSkeleton />
                      </Box>
                    ) : (
                      <>
                        {/* Background Watermark */}
                        {!!proposal?.companyInfo?.ComapanyImageTwo && (
                          <Image
                            src={proposal?.companyInfo?.ComapanyImageTwo}
                            alt="Background"
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            width="70%"
                            height="auto"
                            objectFit="contain"
                            opacity={0.08}
                            zIndex={0}
                            pointerEvents="none"
                            crossOrigin="anonymous"
                          />
                        )}

                        {/* GRADIENT HEADER WITH WAVE */}
                        <Box
                          position="relative"
                          bgGradient="linear(to-br, blue.600, purple.600, pink.500)"
                          overflow="hidden"
                          zIndex={1}
                        >
                          {/* Animated Background Blobs */}
                          <Box
                            position="absolute"
                            top="-20"
                            right="0"
                            w="180px"
                            h="180px"
                            borderRadius="full"
                            bg="whiteAlpha.100"
                            filter="blur(50px)"
                          />
                          <Box
                            position="absolute"
                            bottom="-10"
                            left="10"
                            w="150px"
                            h="150px"
                            borderRadius="full"
                            bg="whiteAlpha.100"
                            filter="blur(40px)"
                          />

                          {/* Header Content */}
                          <Box position="relative" zIndex={1} px={10} py={8}>
                            {/* Company Logo */}
                            <Image
                              src={proposal?.companyInfo?.ComapanyImageOne}
                              w="140px"
                              mb={4}
                              alt="Company Logo"
                              rounded="lg"
                            />

                            {/* Company Details */}
                            <HStack spacing={2} w="full" align="flex-start">
                              <Icon
                                as={MdCompare}
                                boxSize={6}
                                color="white"
                                flexShrink={0}
                                mt={0.5}
                              />
                              <VStack
                                align="start"
                                spacing={0}
                                flex={1}
                                minW={0}
                              >
                                <Text
                                  fontSize="14px"
                                  fontWeight="700"
                                  color="white"
                                  wordBreak="break-word"
                                  w="full"
                                >
                                  {proposal?.companyInfo?.companyName}
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>

                          {/* Bottom Wave */}
                          <Box
                            position="absolute"
                            bottom="-2px"
                            left={0}
                            right={0}
                            height="30px"
                          >
                            <svg
                              viewBox="0 0 1200 30"
                              preserveAspectRatio="none"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <path
                                d="M0,15 Q300,0 600,15 T1200,15 L1200,30 L0,30 Z"
                                fill="white"
                              />
                            </svg>
                          </Box>
                        </Box>

                        {/* Main Content */}
                        <Flex
                          w="full"
                          px={10}
                          pt={6}
                          pb="170px"
                          position="relative"
                          zIndex={1}
                          flexDirection={"column"}
                        >
                          {/* LEFT SIDE - Client Info Card */}
                          <VStack
                            align="stretch"
                            spacing={0}
                            w="280px"
                            flexShrink={0}
                          >
                            {/* Client Card */}
                            <Box
                              bgGradient="linear(to-br, blue.50, purple.50)"
                              border="2px solid"
                              borderColor="blue.100"
                              borderRadius="2xl"
                              p={5}
                              position="relative"
                              overflow="hidden"
                            >
                              {/* Card Background Accent */}
                              <Box
                                position="absolute"
                                top="-40"
                                right="-40"
                                w="200px"
                                h="200px"
                                borderRadius="full"
                                bg="blue.100"
                                opacity={0.1}
                                zIndex={0}
                              />

                              <VStack
                                align="start"
                                spacing={3}
                                position="relative"
                                zIndex={1}
                              >
                                {/* Dear */}
                                <Box>
                                  <Text
                                    fontSize="9px"
                                    fontWeight="700"
                                    color="blue.600"
                                    mb={1}
                                  >
                                    RECIPIENT
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="700"
                                    color="gray.900"
                                    lineHeight="1.3"
                                  >
                                    Dear,{" "}
                                    <Text as="span" color="blue.600">
                                      {proposal?.name}
                                    </Text>
                                  </Text>
                                </Box>

                                <Divider borderColor="blue.200" />

                                {/* Contact Details */}
                                <VStack align="start" spacing={2} w="full">
                                  {/* Email */}
                                  <HStack
                                    spacing={2}
                                    w="full"
                                    align="flex-start"
                                  >
                                    <Icon
                                      as={MdEmail}
                                      boxSize={4}
                                      color="blue.600"
                                      flexShrink={0}
                                      mt="0.5"
                                    />
                                    <VStack
                                      align="start"
                                      spacing={0}
                                      flex={1}
                                      minW={0}
                                    >
                                      <Text
                                        fontSize="8px"
                                        fontWeight="600"
                                        color="gray.500"
                                      >
                                        EMAIL
                                      </Text>
                                      <Text
                                        fontSize="9px"
                                        fontWeight="500"
                                        color="gray.800"
                                        wordBreak="break-word"
                                        w="full"
                                      >
                                        {proposal?.email}
                                      </Text>
                                    </VStack>
                                  </HStack>

                                  {/* Phone */}
                                  <HStack spacing={2} w="full">
                                    <Icon
                                      as={MdPhone}
                                      boxSize={4}
                                      color="green.600"
                                      flexShrink={0}
                                    />
                                    <VStack
                                      align="start"
                                      spacing={0}
                                      flex={1}
                                      minW={0}
                                    >
                                      <Text
                                        fontSize="8px"
                                        fontWeight="600"
                                        color="gray.500"
                                      >
                                        PHONE
                                      </Text>
                                      <Text
                                        fontSize="10px"
                                        fontWeight="500"
                                        color="gray.800"
                                      >
                                        {proposal?.phone}
                                      </Text>
                                    </VStack>
                                  </HStack>

                                  {/* Address */}
                                  <HStack
                                    spacing={2}
                                    align="flex-start"
                                    w="full"
                                  >
                                    <Icon
                                      as={MdLocationOn}
                                      boxSize={4}
                                      color="orange.600"
                                      flexShrink={0}
                                      mt="0.5"
                                    />
                                    <VStack
                                      align="start"
                                      spacing={0}
                                      flex={1}
                                      minW={0}
                                    >
                                      <Text
                                        fontSize="8px"
                                        fontWeight="600"
                                        color="gray.500"
                                      >
                                        ADDRESS
                                      </Text>
                                      <Text
                                        fontSize="10px"
                                        fontWeight="500"
                                        color="gray.800"
                                        lineHeight="1.3"
                                      >
                                        {proposal?.location}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </VStack>
                              </VStack>
                            </Box>
                          </VStack>

                          <Divider
                            borderColor="gray.300"
                            my={4}
                            orientation="vertical"
                            mx={4}
                          />

                          {/* Greeting Content */}
                          <Box
                            fontSize="13px"
                            lineHeight="1.4"
                            color="gray.700"
                            mb={4}
                            dangerouslySetInnerHTML={{
                              __html: proposal?.greeting || "",
                            }}
                            sx={{
                              p: { mb: 2, lineHeight: 1.4 },
                              strong: { fontWeight: 700, color: "gray.900" },
                              "ul, ol": { pl: 5, mb: 2 },
                              li: { mb: 1, lineHeight: 1.4 },
                            }}
                          />

                          {/* Signature */}
                          <Box mt={4}>
                            <Text
                              fontSize="sm"
                              fontWeight="600"
                              color="gray.800"
                              lineHeight="1.4"
                            >
                              Kind Regards,
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="700"
                              color="gray.900"
                              lineHeight="1.4"
                            >
                              {proposal?.companyInfo?.Name}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              lineHeight="1.4"
                            >
                              Mob: {proposal?.companyInfo?.Phone}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="gray.600"
                              lineHeight="1.4"
                            >
                              Email: {proposal?.companyInfo?.Email}
                            </Text>
                            <Text
                              fontSize="sm"
                              fontWeight="600"
                              color="gray.800"
                              mt={1}
                              lineHeight="1.4"
                            >
                              {proposal?.companyInfo?.companyName}
                            </Text>
                          </Box>
                        </Flex>

                        {/* GRADIENT FOOTER */}
                        <Box
                          position="absolute"
                          bottom={0}
                          left={0}
                          right={0}
                          w="full"
                          bgGradient="linear(to-br, blue.600, purple.600, pink.500)"
                          overflow="hidden"
                          zIndex={1}
                        >
                          <Box
                            position="absolute"
                            top="-30"
                            right="-10"
                            w="200px"
                            h="200px"
                            borderRadius="full"
                            bg="whiteAlpha.100"
                            filter="blur(40px)"
                          />
                          <Box
                            position="absolute"
                            bottom="-20"
                            left="-5"
                            w="180px"
                            h="180px"
                            borderRadius="full"
                            bg="whiteAlpha.100"
                            filter="blur(35px)"
                          />

                          {/* Top Wave */}
                          <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            height="20px"
                          >
                            <svg
                              viewBox="0 0 1200 20"
                              preserveAspectRatio="none"
                              style={{ width: "100%", height: "100%" }}
                            >
                              <path
                                d="M0,10 Q300,0 600,10 T1200,10 L1200,0 L0,0 Z"
                                fill="white"
                              />
                            </svg>
                          </Box>

                          <Box
                            position="relative"
                            zIndex={1}
                            px={6}
                            py={5}
                            pt={7}
                          >
                            <SimpleGrid
                              templateColumns="repeat(3, 1fr)"
                              gap={4}
                              fontSize="xs"
                              color="white"
                            >
                              {/* Address Column */}
                              <VStack align="start" spacing={1.5}>
                                <HStack spacing={2}>
                                  <Icon
                                    as={MdLocationOn}
                                    boxSize={4}
                                    color="white"
                                    mt="0.5"
                                  />
                                  <Text fontWeight="700" fontSize="11px">
                                    Address
                                  </Text>
                                </HStack>
                                <Text
                                  lineHeight="1.4"
                                  opacity={0.95}
                                  fontSize="9px"
                                  fontWeight="500"
                                >
                                  {proposal?.companyInfo?.Address}
                                </Text>
                              </VStack>

                              {/* Contact Column */}
                              <VStack align="start" spacing={1.5}>
                                <HStack spacing={2}>
                                  <Icon
                                    as={MdPhone}
                                    boxSize={4}
                                    color="white"
                                    mt="0.5"
                                  />
                                  <Text fontWeight="700" fontSize="11px">
                                    Contact
                                  </Text>
                                </HStack>
                                <VStack align="start" spacing={1}>
                                  <HStack spacing={1}>
                                    <Icon
                                      as={MdPhone}
                                      boxSize={2.5}
                                      opacity={0.8}
                                    />
                                    <Text
                                      opacity={0.95}
                                      fontSize="9px"
                                      fontWeight="500"
                                    >
                                      {proposal?.companyInfo?.Phone}
                                    </Text>
                                  </HStack>
                                  <HStack spacing={1}>
                                    <Icon
                                      as={FaWhatsapp}
                                      boxSize={2.5}
                                      color="green.300"
                                    />
                                    <Text
                                      opacity={0.95}
                                      fontSize="9px"
                                      fontWeight="500"
                                    >
                                      {proposal?.companyInfo?.Phone}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </VStack>

                              {/* Email Column */}
                              <VStack align="start" spacing={1.5}>
                                <HStack spacing={2}>
                                  <Icon
                                    as={MdEmail}
                                    boxSize={4}
                                    color="white"
                                    mt="0.5"
                                  />
                                  <Text fontWeight="700" fontSize="11px">
                                    Contact Info
                                  </Text>
                                </HStack>
                                <VStack align="start" spacing={0.5} w="full">
                                  <HStack spacing={1} w="full" minW={0}>
                                    <Icon
                                      as={MdEmail}
                                      boxSize={2.5}
                                      opacity={0.8}
                                      flexShrink={0}
                                    />
                                    <Text
                                      opacity={0.95}
                                      fontSize="9px"
                                      fontWeight="500"
                                      wordBreak="break-word"
                                      w="full"
                                    >
                                      {proposal?.companyInfo?.Email}
                                    </Text>
                                  </HStack>
                                  <HStack spacing={1} w="full" minW={0}>
                                    <Icon
                                      as={FaGlobe}
                                      boxSize={2.5}
                                      opacity={0.8}
                                      flexShrink={0}
                                    />
                                    <Text
                                      opacity={0.95}
                                      fontSize="9px"
                                      fontWeight="500"
                                      wordBreak="break-word"
                                      w="full"
                                    >
                                      {proposal?.companyInfo?.webURL}
                                    </Text>
                                  </HStack>
                                </VStack>
                              </VStack>
                            </SimpleGrid>

                            <Divider
                              borderColor="whiteAlpha.300"
                              my={3}
                              opacity={0.5}
                            />

                            <HStack justify="center" spacing={2}>
                              <Text
                                fontSize="8px"
                                color="whiteAlpha.800"
                                fontWeight="600"
                                textAlign="center"
                              >
                                © 2025 {proposal?.companyInfo?.companyName}. All
                                rights reserved.
                              </Text>
                            </HStack>
                          </Box>
                        </Box>

                        {/* Page Number */}
                        <Text
                          position="absolute"
                          bottom="155px"
                          right={6}
                          fontSize="10px"
                          color="gray.400"
                          zIndex={2}
                          fontWeight="500"
                        >
                          1
                        </Text>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Page 2: Proposal Description */}
                <Box id="page-2" ref={(el) => (pageRefs.current[2] = el)}>
                  <HtmlPaginated
                    html={proposal?.descriptions?.value || ""}
                    isDownload={isGeneratingPDF}
                    pageNumber={2}
                  />
                </Box>

                {/* Page 3: Items & Pricing */}
                <A4Box
                  imageUrl={proposal?.companyInfo?.ComapanyImageTwo}
                  pageNumber={3}
                >
                  <Box ref={(el) => (pageRefs.current[3] = el)}>
                    {/* Modern Header */}
                    <Flex align="center" gap={3} mb={6}>
                      <Box
                        bgGradient="linear(to-br, purple.400, purple.600)"
                        p={3}
                        borderRadius="xl"
                        boxShadow="md"
                      >
                        <FileText size={20} color="white" />
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                          Proposal Items
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Products and services breakdown
                        </Text>
                      </VStack>
                    </Flex>

                    {/* Items Table */}
                    <VStack spacing={0} mb={6}>
                      {/* Table Header */}
                      <SimpleGrid
                        columns={10}
                        w="100%"
                        bgGradient="linear(to-r, purple.500, purple.600)"
                        borderTopRadius="lg"
                        overflow="hidden"
                        fontWeight="bold"
                        fontSize="xs"
                        color="white"
                      >
                        <GridItem
                          py={3}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="whiteAlpha.300"
                        >
                          #
                        </GridItem>
                        <GridItem
                          colSpan={3}
                          py={3}
                          px={2}
                          borderRight="1px solid"
                          borderColor="whiteAlpha.300"
                        >
                          Product Name
                        </GridItem>
                        <GridItem
                          py={3}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="whiteAlpha.300"
                        >
                          Qty
                        </GridItem>
                        <GridItem
                          py={3}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="whiteAlpha.300"
                        >
                          Price
                        </GridItem>
                        <GridItem
                          colSpan={2}
                          py={3}
                          px={2}
                          textAlign="center"
                          borderRight="1px solid"
                          borderColor="whiteAlpha.300"
                        >
                          Tax
                        </GridItem>
                        <GridItem colSpan={2} py={3} px={2} textAlign="right">
                          Total
                        </GridItem>
                      </SimpleGrid>

                      {/* Table Rows */}
                      {proposal?.items?.map((item: any, index: number) => (
                        <SimpleGrid
                          key={index}
                          columns={10}
                          w="100%"
                          bg={index % 2 === 0 ? "white" : "gray.50"}
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          fontSize="sm"
                          _last={{ borderBottomRadius: "lg" }}
                        >
                          {/* # Column */}
                          <GridItem
                            py={4}
                            px={2}
                            textAlign="center"
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            <Text
                              fontWeight="700"
                              fontSize="13px"
                              color="purple.600"
                            >
                              {index + 1}
                            </Text>
                          </GridItem>

                          {/* Product Name Column */}
                          <GridItem
                            colSpan={3}
                            py={4}
                            px={2}
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            <Text
                              fontWeight="600"
                              color="gray.800"
                              fontSize="13px"
                            >
                              {item?.productName}
                            </Text>
                          </GridItem>

                          {/* Quantity Column */}
                          <GridItem
                            py={4}
                            px={2}
                            textAlign="center"
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            <Text fontWeight="600" fontSize="13px">
                              {item?.quantity}
                            </Text>
                          </GridItem>

                          {/* Price Column */}
                          <GridItem
                            py={4}
                            px={2}
                            textAlign="center"
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            <Text
                              fontWeight="600"
                              color="gray.700"
                              fontSize="13px"
                            >
                              {proposal?.currency}
                              {item?.price}
                            </Text>
                          </GridItem>

                          {/* Tax Column */}
                          <GridItem
                            colSpan={2}
                            py={4}
                            px={2}
                            textAlign="center"
                            borderRight="1px solid"
                            borderColor="gray.200"
                          >
                            <Text
                              fontWeight="700"
                              fontSize="13px"
                              color="blue.600"
                            >
                              {item?.tax}
                            </Text>
                          </GridItem>

                          {/* Total Column */}
                          <GridItem
                            colSpan={2}
                            py={4}
                            px={2}
                            textAlign="right"
                            fontWeight="bold"
                            color="purple.600"
                            fontSize="13px"
                          >
                            {proposal?.currency}
                            {item?.totalWithTax?.toFixed(2)}
                          </GridItem>
                        </SimpleGrid>
                      ))}
                    </VStack>

                    {/* Summary Section */}
                    <Box
                      bg="purple.50"
                      border="2px solid"
                      borderColor="purple.200"
                      borderRadius="lg"
                      p={6}
                      mt={4}
                    >
                      {/* Sub Total */}
                      <Flex justify="space-between" mb={4} align="center">
                        <Text fontSize="16px" fontWeight="700" color="gray.800">
                          Sub Total
                        </Text>
                        <Text fontSize="16px" fontWeight="700" color="gray.800">
                          {proposal?.currency}
                          {proposal?.subTotal?.toFixed(2)}
                        </Text>
                      </Flex>

                      {/* Discount */}
                      <Flex justify="space-between" mb={4} align="center">
                        <HStack spacing={2}>
                          <Text
                            fontSize="16px"
                            fontWeight="700"
                            color="gray.800"
                          >
                            Discount
                          </Text>
                          <Text
                            fontSize="12px"
                            fontWeight="600"
                            color="green.600"
                          >
                            {proposal?.discountType === "percentage"
                              ? `${proposal?.discount}%`
                              : "Fixed"}
                          </Text>
                        </HStack>
                        <Text fontSize="16px" fontWeight="700" color="red.600">
                          -{proposal?.currency}
                          {proposal?.discountPrice?.toFixed(2)}
                        </Text>
                      </Flex>

                      <Divider borderColor="purple.300" mb={4} />

                      {/* Total Tax */}
                      <Flex justify="space-between" mb={4} align="center">
                        <Text fontSize="14px" fontWeight="600" color="gray.700">
                          Total Tax
                        </Text>
                        <Text fontSize="14px" fontWeight="600" color="gray.700">
                          {proposal?.currency}
                          {proposal?.totalTax?.toFixed(2)}
                        </Text>
                      </Flex>

                      {/* Grand Total */}
                      <Flex justify="space-between" align="center">
                        <Text
                          fontSize="18px"
                          fontWeight="900"
                          color="purple.600"
                        >
                          Total
                        </Text>
                        <Text
                          fontSize="18px"
                          fontWeight="900"
                          color="purple.600"
                        >
                          {proposal?.currency}
                          {proposal?.total?.toFixed(2)}
                        </Text>
                      </Flex>
                    </Box>
                  </Box>
                </A4Box>

                {/* Page 4: Payment Plan - Similar to ProposalView */}
                <A4Box
                  imageUrl={proposal?.companyInfo?.ComapanyImageTwo}
                  pageNumber={4}
                >
                  <Box ref={(el) => (pageRefs.current[4] = el)}>
                    {/* Modern Header */}
                    <Flex align="center" gap={3} mb={6}>
                      <Box
                        bgGradient="linear(to-br, green.400, green.600)"
                        p={3}
                        borderRadius="xl"
                        boxShadow="md"
                      >
                        <Icon
                          viewBox="0 0 24 24"
                          boxSize={5}
                          color="white"
                          fill="currentColor"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                        </Icon>
                      </Box>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                          Payment Plan
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          EMI schedule and payment tracking
                        </Text>
                      </VStack>
                    </Flex>

                    {/* EMI Table */}
                    <VStack spacing={0} mb={6}>
                      {/* Header */}
                      <SimpleGrid
                        w="100%"
                        bgGradient="linear(to-r, green.500, green.600)"
                        p={4}
                        borderTopRadius="lg"
                        columns={6}
                        alignItems="center"
                        fontWeight="bold"
                        fontSize="xs"
                        color="white"
                      >
                        <Text textAlign="center">#</Text>
                        <Text textAlign="center">EMI %</Text>
                        <Text textAlign="center">Amount</Text>
                        <Text textAlign="center">Status</Text>
                        <Text textAlign="center">Emi Payment Date</Text>
                        <Text textAlign="center">Paid Date</Text>
                      </SimpleGrid>

                      {/* Rows */}
                      {proposal?.emiSteps?.map((emi, idx) => (
                        <SimpleGrid
                          key={idx}
                          w="100%"
                          bg={idx % 2 === 0 ? "white" : "gray.50"}
                          p={4}
                          columns={6}
                          alignItems="center"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          fontSize="sm"
                          _last={{ borderBottomRadius: "lg" }}
                        >
                          <Text textAlign="center" fontWeight="600">
                            {emi?.label}
                          </Text>
                          <Text
                            textAlign="center"
                            fontWeight="bold"
                            color="green.600"
                          >
                            {emi?.payPercentage}%
                          </Text>
                          <Text
                            textAlign="center"
                            fontWeight="bold"
                            color="gray.800"
                          >
                            {proposal?.currency}
                            {emi?.pay?.toFixed(2)}
                          </Text>
                          <Box display="flex" justifyContent="center">
                            <Text
                              fontWeight="700"
                              fontSize="12px"
                              color={emi?.isPaid ? "green.600" : "red.600"}
                              px={2}
                              py={1}
                            >
                              {emi?.isPaid ? "PAID" : "PENDING"}
                            </Text>
                          </Box>
                          <Text
                            textAlign="center"
                            color="gray.600"
                            fontSize="xs"
                          >
                            {emi?.emiPaymentDate
                              ? new Date(
                                  emi?.emiPaymentDate
                                ).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "--"}
                          </Text>
                          <Text
                            textAlign="center"
                            color="gray.600"
                            fontSize="xs"
                          >
                            {emi?.isPaid && emi?.emiPaymentReceivedDate
                              ? new Date(
                                  emi?.emiPaymentReceivedDate
                                ).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "--"}
                          </Text>
                        </SimpleGrid>
                      ))}
                    </VStack>

                    {/* Payment Summary Cards */}
                    <SimpleGrid columns={3} spacing={4}>
                      {/* Paid */}
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="xl"
                        boxShadow="md"
                        border="2px solid"
                        borderColor="green.200"
                        position="relative"
                        overflow="hidden"
                      >
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          w="full"
                          h="4px"
                          bgGradient="linear(to-r, green.400, green.600)"
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.500"
                          mb={2}
                        >
                          Paid Amount
                        </Text>
                        <Text fontSize="xl" fontWeight="900" color="green.600">
                          {proposal?.currency}
                          {proposal?.emiSteps
                            ?.filter((emi) => emi.isPaid)
                            ?.reduce((acc, curr) => acc + curr.pay, 0)
                            .toFixed(2)}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {
                            proposal?.emiSteps?.filter((emi) => emi.isPaid)
                              ?.length
                          }{" "}
                          payments received
                        </Text>
                      </Box>

                      {/* Remaining */}
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="xl"
                        boxShadow="md"
                        border="2px solid"
                        borderColor="red.200"
                        position="relative"
                        overflow="hidden"
                      >
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          w="full"
                          h="4px"
                          bgGradient="linear(to-r, red.400, red.600)"
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.500"
                          mb={2}
                        >
                          Remaining
                        </Text>
                        <Text fontSize="xl" fontWeight="900" color="red.600">
                          {proposal?.currency}
                          {proposal?.emiSteps
                            ?.filter((emi) => !emi.isPaid)
                            ?.reduce((acc, curr) => acc + curr.pay, 0)
                            .toFixed(2)}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {
                            proposal?.emiSteps?.filter((emi) => !emi.isPaid)
                              ?.length
                          }{" "}
                          payments pending
                        </Text>
                      </Box>

                      {/* Total */}
                      <Box
                        p={4}
                        bg="white"
                        borderRadius="xl"
                        boxShadow="md"
                        border="2px solid"
                        borderColor="blue.200"
                        position="relative"
                        overflow="hidden"
                      >
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          w="full"
                          h="4px"
                          bgGradient="linear(to-r, blue.400, blue.600)"
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.500"
                          mb={2}
                        >
                          Total Amount
                        </Text>
                        <Text fontSize="xl" fontWeight="900" color="blue.600">
                          {proposal?.currency}
                          {proposal?.total?.toFixed(2)}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Full proposal value
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                </A4Box>

                {/* Page 5: Terms & Signature */}
                <A4Box
                  imageUrl={proposal?.companyInfo?.ComapanyImageTwo}
                  pageNumber={5}
                >
                  <Box ref={(el) => (pageRefs.current[5] = el)}>
                    <Text fontSize="xl" fontWeight="bold" mb={4}>
                      Terms & Conditions
                    </Text>
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: proposal?.terms?.value || "",
                      }}
                      fontSize="md"
                    />

                    <Flex mt={6} justify="space-between" align="flex-end">
                      <Box mt={10}>
                        <Text fontWeight="bold" fontSize="lg">
                          Thank you.
                        </Text>
                        <Text fontWeight="bold">
                          {proposal?.companyInfo?.companyName}
                        </Text>
                        <Text>Mob: {proposal?.companyInfo?.Phone}</Text>
                        <Text>Email: {proposal?.companyInfo?.Email}</Text>
                        <Text whiteSpace="nowrap">
                          Date:{" "}
                          {proposal?.sentDate
                            ? new Date(proposal?.sentDate).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )
                            : ""}
                        </Text>
                      </Box>

                      {(proposal?.status === "ACCEPTED" ||
                        proposal?.status === "DECLINED") && (
                        <VStack align="flex-end" justify="flex-start">
                          <Tag
                            size="lg"
                            colorScheme={
                              proposal?.status === "ACCEPTED" ? "green" : "red"
                            }
                          >
                            {proposal?.status}
                          </Tag>
                          <Text>
                            <strong>Note: </strong>
                            {proposal?.clientNote}
                          </Text>
                          <Image
                            crossOrigin="anonymous"
                            src={proposal?.Esign}
                            width="30%"
                            alt="Signature"
                          />
                          <Text fontWeight="bold">
                            Signature - {proposal?.name}
                          </Text>
                          <Text>
                            Date:{" "}
                            {proposal?.clientReactionDate
                              ? new Date(
                                  proposal?.clientReactionDate
                                ).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                              : ""}
                          </Text>
                        </VStack>
                      )}
                    </Flex>
                  </Box>
                </A4Box>
              </A4ResponsiveWrapper>
            </VStack>
          )}

          {/* E-Sign Canvas */}
          {showAccept && (
            <Box w="full" minH="100vh" bg="white" p={4}>
              <ESignCanvas
                back={setShowAccept}
                proposalId={proposalId}
                onSuccess={() => setShowThankYou(true)}
              />
            </Box>
          )}

          {/* Decline Form */}
          {showDecline && (
            <Box w="full" minH="100vh" bg="white" p={4}>
              <DeclineForm
                back={setShowDecline}
                proposalId={proposalId}
                onSuccess={() => setShowSeeYouNext(true)}
              />
            </Box>
          )}
        </Box>
      </Flex>

      {/* Fixed Footer with Action Buttons - Hidden in print */}
      {!showAccept &&
        proposal?.status !== "ACCEPTED" &&
        proposal?.status !== "DECLINED" && (
          <Box
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            p={4}
            bg="white"
            borderTop="1px"
            borderColor="gray.200"
            boxShadow="lg"
            zIndex={1000}
            className="no-print"
          >
            <Flex justify="center" gap={4} maxW="1400px" mx="auto">
              <Button
                leftIcon={<CheckCircle size={16} />}
                onClick={() => setShowAccept(true)}
                size="lg"
                fontSize="sm"
                fontWeight="700"
                bgGradient="linear(to-r, green.400, green.600)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, green.500, green.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px -4px rgba(34, 197, 94, 0.4)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s"
                flex={{ base: 1, md: "initial" }}
                maxW="300px"
              >
                ACCEPT PROPOSAL
              </Button>
              <Button
                leftIcon={<FaTimes />}
                onClick={() => setShowDecline(true)}
                size="lg"
                fontSize="sm"
                fontWeight="700"
                bgGradient="linear(to-r, red.400, red.600)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, red.500, red.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px -4px rgba(239, 68, 68, 0.4)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s"
                flex={{ base: 1, md: "initial" }}
                maxW="300px"
              >
                DECLINE PROPOSAL
              </Button>
            </Flex>
          </Box>
        )}

      {/* Already Accepted/Declined Banner */}
      {(proposal?.status === "ACCEPTED" || proposal?.status === "DECLINED") && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          bg={proposal?.status === "ACCEPTED" ? "green.500" : "red.500"}
          color="white"
          p={4}
          textAlign="center"
          fontWeight="bold"
          zIndex={1000}
          className="no-print"
        >
          {proposal?.status === "ACCEPTED"
            ? "✓ YOU HAVE ALREADY ACCEPTED THIS PROPOSAL"
            : "✗ YOU HAVE ALREADY DECLINED THIS PROPOSAL"}
        </Box>
      )}
    </Box>
  );
};

export default ClientProposalView;
